import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { mockLicenses } from '../../../lib/mock-data';
import { SkeletonCard, SkeletonTable } from '../../../components/common/SkeletonLoader';

/**
 * Admin Licenses - System-wide license oversight
 */
export const Route = createFileRoute('/_authenticated/admin/licenses')({
  component: AdminLicenses,
});

function AdminLicenses() {
  const [licenses, setLicenses] = useState<typeof mockLicenses>([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setLicenses(mockLicenses);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      dashedBorder: 'rgba(220, 38, 38, 0.3)',
      tableBg: 'rgba(15, 15, 20, 0.4)',
      tableHeaderBg: 'rgba(26, 26, 46, 0.6)',
      inputBg: 'rgba(26, 26, 46, 0.6)',
      inputBorder: 'rgba(220, 38, 38, 0.3)',
      headerColor: '#ef4444'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      dashedBorder: '#e5e7eb',
      tableBg: '#ffffff',
      tableHeaderBg: '#f9fafb',
      inputBg: '#ffffff',
      inputBorder: '#e5e7eb',
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.headerColor,
            marginBottom: '0.5rem'
          }}>
            License Oversight
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Monitor and manage all licenses across the platform
          </p>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {loading ? (
            <>
              <SkeletonCard theme={theme} />
              <SkeletonCard theme={theme} />
              <SkeletonCard theme={theme} />
              <SkeletonCard theme={theme} />
            </>
          ) : (
            <>
              <StatCard title="Total Licenses" value={licenses.length.toString()} color={c.text} theme={theme} />
              <StatCard title="Active" value={licenses.filter(l => l.status === 'active').length.toString()} color="#059669" theme={theme} />
              <StatCard title="Suspended" value={licenses.filter(l => l.status === 'suspended').length.toString()} color="#dc2626" theme={theme} />
              <StatCard title="Expired" value={licenses.filter(l => l.status === 'expired').length.toString()} color="#6b7280" theme={theme} />
            </>
          )}
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <input
            type="text"
            placeholder="Search by license key or user..."
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              border: `1px solid ${c.inputBorder}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: c.inputBg,
              color: c.text
            }}
          />
          <select style={{
            padding: '0.75rem 1rem',
            border: `1px solid ${c.inputBorder}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: c.inputBg,
            color: c.text
          }}>
            <option>All Status</option>
            <option>Active</option>
            <option>Suspended</option>
            <option>Expired</option>
          </select>
        </div>

        {/* Licenses Table */}
        {loading ? (
          <SkeletonTable rows={6} columns={8} theme={theme} />
        ) : licenses.length === 0 ? (
          <div style={{
            background: c.cardBg,
            border: `2px dashed ${c.dashedBorder}`,
            borderRadius: '0.5rem',
            padding: '3rem',
            textAlign: 'center',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{
              fontSize: '1.125rem',
              color: c.textMuted,
              marginBottom: '1rem'
            }}>
              No licenses found
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              opacity: 0.7
            }}>
              Licenses will appear here as users make purchases
            </p>
          </div>
        ) : (
          <div style={{
            background: c.tableBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: c.tableHeaderBg }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>License Key</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>User</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Creator</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Activations</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Issued</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.key} style={{ borderTop: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: "'JetBrains Mono', monospace", color: c.text, letterSpacing: '-0.02em' }}>
                      {license.key}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{license.user}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{license.product}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{license.creator}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>
                      {license.activations}/{license.maxActivations}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{license.issued}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: license.status === 'active' ? '#d1fae5' :
                                       license.status === 'suspended' ? '#fee2e2' : '#f3f4f6',
                        color: license.status === 'active' ? '#065f46' :
                               license.status === 'suspended' ? '#991b1b' : '#6b7280'
                      }}>
                        {license.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <button style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: theme === 'dark' ? 'rgba(220, 38, 38, 0.2)' : '#fef2f2',
                        border: `1px solid ${c.cardBorder}`,
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        color: c.headerColor
                      }}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  color: string;
  theme: 'dark' | 'light';
}

function StatCard({ title, value, color, theme }: StatCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      textMuted: '#9ca3af'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      textMuted: '#6b7280'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
    }}>
      <p style={{ fontSize: '0.75rem', color: c.textMuted, marginBottom: '0.5rem' }}>
        {title}
      </p>
      <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color }}>
        {value}
      </p>
    </div>
  );
}

export default AdminLicenses;
